import { useState, useEffect } from "react";
import firebase, {
  axiosWithToken,
  functionBaseUrl,
} from "../../../common/firebase";
import { ENVIRONMENT } from "../../../common/envConfig";

/**
 * Custom hook for cohort analysis data fetching
 */
export const useCohortAnalysis = ({
  subscriptions: inputSubscriptions,
  productType,
  subType,
  infantChurn,
}) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [loadedSubscriptions, setLoadedSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState(null);

  useEffect(() => {
    if (inputSubscriptions !== undefined) return;

    let cancelled = false;
    const load = async () => {
      const subs = await loadSubscriptions();
      if (!cancelled && Array.isArray(subs)) {
        setLoadedSubscriptions(subs);
        if (subs.length === 0) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [inputSubscriptions]);

  const effectiveSubscriptions =
    inputSubscriptions !== undefined ? inputSubscriptions : loadedSubscriptions;

  useEffect(() => {
    if (inputSubscriptions !== undefined && inputSubscriptions.length === 0) {
      setLoading(false);
      return;
    }
    if (effectiveSubscriptions?.length > 0) {
      loadCohortData(effectiveSubscriptions);
    }
  }, [productType, subType, effectiveSubscriptions, infantChurn]);

  const loadSubscriptions = async () => {
    // Matches old SummaryGraphs logic
    const isTestAccount = (email, deviceLimitExceptionList = []) => {
      if (!email) return false;
      const mustNotTerms = [/sb3d/, /3dsb/, /sportsbox/, /demo/, /_sb3d/];
      return (
        deviceLimitExceptionList?.includes(email) ||
        mustNotTerms.some((term) => term.test(email))
      );
    };

    try {
      setSubsLoading(true);
      setSubsError(null);

      // OLD: only load exception list in production
      let exceptionList = [];
      try {
        // If ENVIRONMENT isn’t available here, you can either:
        // - import it from envConfig (recommended), or
        // - temporarily always load it (but that differs from old behavior).
        //
        // Recommended:
        // import { ENVIRONMENT } from "../../../common/envConfig";
        // if (ENVIRONMENT === "production") { ... }
        //
        // For now, assume ENVIRONMENT is in scope; otherwise wrap with a try.
        if (typeof ENVIRONMENT === "string" && ENVIRONMENT === "production") {
          const deviceSnap = await firebase
            .firestore()
            .collection("mobileAppSettings")
            .doc("X3X42cbYDIT9t9BL43bz")
            .get();

          exceptionList = deviceSnap?.data()?.deviceLimitExceptionList || [];
        }
      } catch (err) {
        console.warn("Could not load deviceLimitExceptionList:", err);
      }

      // OLD: fetch ALL subscriptions
      const subsSnap = await firebase
        .firestore()
        .collection("subscriptions")
        .get();

      const allSubs = [];
      subsSnap.forEach((doc) => {
        const s = doc.data();
        if (!isTestAccount(s?.userEmail, exceptionList)) {
          allSubs.push({ ...s, id: doc.id });
        }
      });

      // OLD: cohort cutoff in SummaryGraphs was 1672560000000 (Jan 1, 2023)
      const COHORT_CUTOFF_MS = 1672560000000;

      const cohortSubsResult = allSubs
        .filter((sub) => Number(sub.startDate) >= COHORT_CUTOFF_MS)
        .map((s) => ({
          startDate: s.startDate,
          endDate: s.endDate,
          productId: s.productId,
          transactionId: s.transactionId,
          subLabel: s.subLabel,
        }));

      setLoadedSubscriptions(cohortSubsResult);
      return cohortSubsResult;
    } catch (err) {
      console.error("Error loading cohort subscriptions:", err);
      setSubsError(err);
      setLoadedSubscriptions([]);
      return [];
    } finally {
      setSubsLoading(false);
    }
  };

  const loadCohortData = async (subscriptions) => {
    setLoading(true);
    setError(null);
    setData({});

    try {
      const response = await axiosWithToken(
        `${functionBaseUrl}/api/cohortHelper`,
        {
          method: "POST",
          data: {
            productType,
            subscriptions,
            subType,
            infantChurn,
          },
        },
      );

      const cleanRetentionRates = (rates) =>
        rates.map((rate) => parseFloat(rate.replace("%", "")));

      const result = {
        months: Object.fromEntries(
          response.data.map(({ cohort, retentionRates, newAccountsSold }) => {
            const cohortLabel = `${cohort} (${newAccountsSold})`;
            return [cohortLabel, cleanRetentionRates(retentionRates)];
          }),
        ),
      };

      setData(result);
    } catch (err) {
      console.error("Error loading cohort data:", err);
      setError(err.message || "Failed to load cohort data");
    } finally {
      setLoading(false);
    }
  };

  const refetch = async () => {
    const subs = await loadSubscriptions();
    if (subs?.length > 0) {
      await loadCohortData(subs);
    }
  };

  return {
    data,
    loading: loading || subsLoading,
    error: error || subsError,
    refetch,
  };
};
